import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"

/**
 * Betriebsregeln je Betrieb (#86, Paul 26.10.2026).
 *
 * Bedingung (Absender, Stichworte zum Thema/Gewerk, Gewerk, Terminart, Anlass) →
 * Anweisung, Partner oder Aufgabe an eine Person. Absender aus einer Regel
 * räumt die Archiv-Automatik nie als Werbung/Spam weg (Paul 02.11.2026). Der Planer bekommt nur die Regeln, die zur Mail
 * passen; mit „Termin an Partner weiterleiten“ schlägt er bei einem
 * bestätigten Termin den Weiterleiten-Schritt vor (erst per Klick).
 *
 * 🔴 Nur mit Recht „betriebsregeln“ — die Sperre sitzt im Backend.
 */

export type Betriebsregel = {
  id: string
  aktiv: boolean
  titel: string
  stichworte: string[]
  gewerk: string
  terminart: string
  anlass: string
  anweisung: string
  aktion: string
  partner_name: string
  partner_email: string
  partner_domain: string
  absender: string[]
  aufgabe_user_id: number | null
  aufgabe_user_name: string
}

type Person = { id: number; hero_user_id: number | null; name: string }

type Antwort = { tenant: string; regeln: Betriebsregel[] }

const LEER: Betriebsregel = {
  id: "", aktiv: true, titel: "", stichworte: [], gewerk: "", terminart: "",
  anlass: "immer", anweisung: "", aktion: "hinweis", partner_name: "", partner_email: "", partner_domain: "",
  absender: [], aufgabe_user_id: null, aufgabe_user_name: "",
}

async function laden(tenant: string): Promise<Antwort> {
  const r = await fetch(`/api/v1/betriebsregeln?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function speichern(tenant: string, regeln: Betriebsregel[]): Promise<Antwort> {
  const r = await fetch(`/api/v1/betriebsregeln?tenant=${encodeURIComponent(tenant)}`, {
    method: "PUT", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ regeln }),
  })
  if (!r.ok) {
    let grund = `HTTP ${r.status}`
    try { grund = (await r.json()).detail || grund } catch { /* Text bleibt */ }
    throw new Error(grund)
  }
  return r.json()
}

const feld = { fontSize: 13, width: "100%" } as const
const label = { fontSize: 12 } as const

export function BetriebsregelnCard({ tenant }: { tenant: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["betriebsregeln", tenant], queryFn: () => laden(tenant), enabled: !!tenant })
  const [items, setItems] = useState<Betriebsregel[]>([])
  const [saving, setSaving] = useState(false)
  // Wer eine Aufgabe bekommen kann: nur Mitarbeiter mit HERO-User-ID (Aufgaben ≠ Termine).
  const personen = useQuery({
    queryKey: ["monteure", tenant],
    queryFn: async () => {
      const r = await fetch(`/api/v1/calendar/monteure?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      return r.ok ? ((await r.json()).items || []) as Person[] : []
    },
    enabled: !!tenant,
  })
  const mitUser = (personen.data || []).filter((p) => p.hero_user_id != null)
  useEffect(() => { if (q.data) setItems(q.data.regeln) }, [q.data])

  if (q.isLoading) return <Loading label="Lade Betriebsregeln…" />
  if (q.isError) return <ErrorBox error={q.error} />
  const dirty = JSON.stringify(items) !== JSON.stringify(q.data?.regeln || [])
  const update = (i: number, patch: Partial<Betriebsregel>) =>
    setItems(items.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  return (
    <div className="pp-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Betriebsregeln für den Planer</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, marginBottom: 12 }}>
        Wie der Betrieb arbeitet, z. B. „Bad-Besichtigungen macht der Montagepartner; bestätigte
        Besichtigungstermine an ihn weiterleiten“. Der Planer bekommt nur die Regeln, deren Bedingung
        zur Mail passt. Mit „Termin an Partner weiterleiten“ erscheint bei einem bestätigten Termin
        der Schritt zum Weiterleiten — ausgeführt wird er erst per Klick.
      </div>

      {items.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
          Noch keine Betriebsregeln hinterlegt.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((r, i) => (
          <div key={r.id || i} style={{
            border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 8, padding: 12,
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, opacity: r.aktiv ? 1 : 0.6,
          }}>
            <div style={{ gridColumn: "1 / 3" }}>
              <label className="pp-form-label" style={label}>Titel</label>
              <input value={r.titel} onChange={(e) => update(i, { titel: e.target.value })}
                placeholder="Bad-Besichtigungen macht Partner X" style={feld} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, justifyContent: "flex-end" }}>
              <label style={{ fontSize: 12, display: "flex", gap: 4, alignItems: "center" }}>
                <input type="checkbox" checked={r.aktiv} onChange={(e) => update(i, { aktiv: e.target.checked })} />
                aktiv
              </label>
              <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                style={{ background: "transparent", border: "0.5px solid #D85A30", color: "#D85A30",
                  borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer", height: 32 }}>
                Löschen
              </button>
            </div>

            <div style={{ gridColumn: "1 / 4" }}>
              <label className="pp-form-label" style={label}>Absender (Adresse oder Domain, komma-getrennt — leer = egal)</label>
              <input value={r.absender.join(", ")}
                onChange={(e) => update(i, { absender: e.target.value.split(/[,;\s]+/).map((t) => t.trim()).filter(Boolean) })}
                placeholder="steuerbuero.de, chef@lieferant.de" style={feld} />
              {r.absender.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  Mails dieser Absender verschiebt die Automatik nie in Spam oder Werbung.
                </div>
              )}
            </div>
            <div>
              <label className="pp-form-label" style={label}>Stichworte Thema (komma-getrennt)</label>
              <input value={r.stichworte.join(", ")}
                onChange={(e) => update(i, { stichworte: e.target.value.split(/[,;]/).map((t) => t.trim()).filter(Boolean) })}
                placeholder="badsanierung, badezimmer" style={feld} />
            </div>
            <div>
              <label className="pp-form-label" style={label}>Gewerk (optional)</label>
              <input value={r.gewerk} onChange={(e) => update(i, { gewerk: e.target.value })}
                placeholder="z. B. Sanitär" style={feld} />
            </div>
            <div>
              <label className="pp-form-label" style={label}>Terminart</label>
              <select value={r.terminart} onChange={(e) => update(i, { terminart: e.target.value })} style={feld}>
                <option value="">egal</option>
                <option value="besichtigung">Besichtigung</option>
                <option value="wartung">Wartung</option>
                <option value="reparatur">Reparatur</option>
              </select>
            </div>

            <div style={{ gridColumn: "1 / 4" }}>
              <label className="pp-form-label" style={label}>Anweisung an den Planer</label>
              <textarea value={r.anweisung} onChange={(e) => update(i, { anweisung: e.target.value })} rows={2}
                placeholder="Was soll bei solchen Mails passieren?" style={{ ...feld, resize: "vertical" }} />
            </div>

            <div>
              <label className="pp-form-label" style={label}>Aktion</label>
              <select value={r.aktion} onChange={(e) => update(i, { aktion: e.target.value })} style={feld}>
                <option value="hinweis">nur Anweisung</option>
                <option value="termin_an_partner">Termin an Partner weiterleiten</option>
                <option value="aufgabe_an">Aufgabe an Person</option>
              </select>
            </div>
            <div>
              <label className="pp-form-label" style={label}>Wann</label>
              <select value={r.anlass} onChange={(e) => update(i, { anlass: e.target.value })} style={feld}>
                <option value="immer">immer, wenn die Bedingung passt</option>
                <option value="termin_bestaetigt">wenn der Termin bestätigt ist</option>
              </select>
            </div>
            {r.aktion === "aufgabe_an" ? (
              <div>
                <label className="pp-form-label" style={label}>Aufgabe an</label>
                <select aria-label="Aufgabe an" value={r.aufgabe_user_id ?? ""} style={feld}
                  onChange={(e) => {
                    const uid = e.target.value ? Number(e.target.value) : null
                    const p = mitUser.find((m) => m.hero_user_id === uid)
                    update(i, { aufgabe_user_id: uid, aufgabe_user_name: p?.name || "" })
                  }}>
                  <option value="">— Person wählen —</option>
                  {mitUser.map((m) => <option key={m.hero_user_id!} value={m.hero_user_id!}>{m.name}</option>)}
                  {r.aufgabe_user_id != null && !mitUser.some((m) => m.hero_user_id === r.aufgabe_user_id) && (
                    <option value={r.aufgabe_user_id}>{r.aufgabe_user_name || "gespeicherte Person"}</option>
                  )}
                </select>
              </div>
            ) : <div />}

            {r.aktion === "aufgabe_an" && (
              <div style={{ gridColumn: "1 / 4", fontSize: 11, color: "var(--text-muted)" }}>
                Passt die Mail, schlägt der Plan „Aufgabe anlegen“ für diese Person vor — angelegt wird
                sie erst per Klick. Die Mail steht dann nie unter „Zur Kenntnis“.
              </div>
            )}

            {r.aktion === "termin_an_partner" && (
              <>
                <div>
                  <label className="pp-form-label" style={label}>Partner</label>
                  <input value={r.partner_name} onChange={(e) => update(i, { partner_name: e.target.value })}
                    placeholder="Montagepartner GmbH" style={feld} />
                </div>
                <div>
                  <label className="pp-form-label" style={label}>Partner-E-Mail</label>
                  <input type="email" value={r.partner_email}
                    onChange={(e) => update(i, { partner_email: e.target.value.trim() })}
                    placeholder="termine@partner.example" style={feld} />
                </div>
                <div>
                  <label className="pp-form-label" style={label}>oder nur Domain</label>
                  <input value={r.partner_domain}
                    onChange={(e) => update(i, { partner_domain: e.target.value.trim() })}
                    placeholder="partner.example" style={feld} />
                </div>
                <div style={{ gridColumn: "1 / 4", fontSize: 11, color: "var(--text-muted)" }}>
                  Nur mit Domain sucht das Portal die Adresse in HERO und im Mailverlauf. Ist sie nicht
                  eindeutig, steht im Plan eine Rückfrage statt des Schritts.
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" onClick={() => setItems([...items, { ...LEER }])} className="pp-btn pp-btn-sm">
          + Regel hinzufügen
        </button>
        <button type="button" disabled={!dirty || saving} className="pp-btn pp-btn-primary pp-btn-sm"
          onClick={async () => {
            setSaving(true)
            try {
              const neu = await speichern(tenant, items)
              qc.setQueryData(["betriebsregeln", tenant], neu)
              toast("✓ Betriebsregeln gespeichert")
            } catch (e) {
              toast(`Nicht gespeichert: ${(e as Error).message}`, "error")
            } finally { setSaving(false) }
          }}>
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}
